import { finding, inspection, isNull, sql, template } from "@fieldcheck/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CAPA LIVE CENTRALIZADA (choke point único)                  │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Este módulo es el ÚNICO lugar donde el invariante «fila viva = `deleted_at IS
 * NULL`» se expresa para las 3 tablas con papelera de este repo (verificadas
 * leyendo `packages/powersync/src/schema.ts`, no asumidas):
 *   inspection, finding, template.
 * Toda lectura viva de estas tablas compone estos predicados (los builders de
 * `fieldwork/queries.ts` y cualquier otra vista viva los importan de aquí). Las
 * ÚNICAS lecturas que ven filas borradas son las de la papelera
 * (`fieldwork/queries.ts#getTrashed*Query`, whitelisted como "trash") y los
 * lookups id-scoped inofensivos (detalle por id / read-before-write, "keyed").
 *
 * ¿Por qué centralizar? El invariante disperso ya causaba filtros inline
 * repetidos (`isNull(x.deleted_at)`) en cada builder de listado + un contador
 * CRUDO en `checkUnsyncedActor` (contaba filas en papelera). El guard estático
 * (`__tests__/live-access-guard.test.ts` + `guard-manifest.ts`) impide la
 * regresión: cualquier lectura nueva fuera de la capa live rompe el build.
 *
 * ─── field-check es B2B INMUTABLE (compliance) ─────────────────────────────
 * La papelera es SOLO-RESTAURAR: no existe "eliminar definitivamente" ni job de
 * purga (la evidencia de inspección/hallazgo/plantilla no se destruye jamás —
 * el hash-chain de `activity` es un rastro de auditoría append-only). La capa
 * live es una preocupación de LECTURA, ortogonal al historial de actividad.
 *
 * ─── Jerarquía de cohortes (dominio FieldCheck) ────────────────────────────
 * Verificada contra los FKs LÓGICOS de `packages/powersync/src/schema.ts` (no
 * hay FKs físicos en el SQLite de PowerSync: se modelan lógicamente):
 *   - template   → RAÍZ (sólo `organization_id`/`user_id`). No cuelga de ninguna
 *                  otra tabla soft-delete.
 *   - inspection → RAÍZ. `template_id`→template y `site_id`→site: una inspección
 *                  es un registro INDEPENDIENTE (evidencia sellada) que sobrevive
 *                  a que su plantilla se archive/borre, y `site` NO es
 *                  soft-deletable — por eso inspection no lleva cohorte de padre.
 *   - finding    → hijo de inspection (`inspection_id`, OPCIONAL: un hallazgo
 *                  puede ser de sitio sin inspección). Si su inspección está en
 *                  la papelera, el hallazgo desaparece de las vistas vivas y
 *                  reaparece al restaurar la inspección (cohorte). Con
 *                  `inspection_id` NULL el NOT EXISTS resuelve a verdadero (no
 *                  oculta la fila).
 *
 * NOTA (cohorte de un nivel, patrón del piloto/comandero/stockpilot): la cohorte
 * comprueba el PADRE DIRECTO, sin alias de tabla. El self-join con alias de
 * drizzle rompe `resolveTables` de PowerSync en runtime (ver el gotcha
 * documentado en stockpilot): aquí no aplica porque finding→inspection son
 * tablas distintas y el predicado usa el nombre literal de la tabla padre.
 */

/**
 * Predicado de cohorte genérico: la fila hija cuelga de un padre que NO está en
 * la papelera. Con soft-delete del padre los hijos persisten intactos (para
 * restaurar la cohorte entera) pero desaparecen de las vistas vivas. Si
 * `childFk` es NULL (FK opcional, p. ej. `finding.inspection_id` de un hallazgo
 * de sitio), el NOT EXISTS resuelve a verdadero — no oculta la fila.
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Raíces (sólo su propio deleted_at) ──────────────────────────────────────

/** Plantillas VIVAS (raíz: no cuelga de otra tabla soft-delete). */
export function liveTemplateFilters() {
  return [isNull(template.deleted_at)];
}

/**
 * Inspecciones VIVAS (raíz: una inspección es evidencia independiente; su
 * plantilla/sitio no la arrastran a la papelera).
 */
export function liveInspectionFilters() {
  return [isNull(inspection.deleted_at)];
}

// ─── Hijos con cohorte (padre con papelera no borrado) ───────────────────────

/**
 * Hallazgos VIVOS: su propio `deleted_at` + su inspección (cuando existe) no
 * está en la papelera. Reaparecen al restaurar la inspección.
 */
export function liveFindingFilters() {
  return [isNull(finding.deleted_at), parentNotTrashed(inspection, finding.inspection_id)];
}
